import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useDraggable } from 'react-use-draggable-scroll';
import { ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import {
  getActiveQuickAccess,
  getExplodedIAMTree,
  getExplodedTree,
  getLastVehicleDetail,
  setIamInitialTextFilter,
  setQuickAccessActiveCategory,
  setQuickAccessActiveSubCategory,
} from 'domains/catalog/Catalog.store';
import { OEM_BRAND_KEY, OTHER_BRAND_KEY } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { getIAMTires, getVehicleTires } from 'domains/tires/Tire.store';
import { getIamCatalogBrandsView } from 'domains/user';
import {
  Box,
  CenteredSpin,
  CenterFlex,
  Flex,
  Icon,
  IconType,
  Link,
  QuickAccessScrollBackButton,
  QuickAccessScrollForwardButton,
  Text,
  WithTooltip,
} from 'UI';
import { LOADING, isLoading, useBreakpointSelector, useLarge } from 'utils';

import {
  CategoryFlex,
  QUICK_ACCESS_CATEGORY_HEIGHT,
  QuickAccessArrowSlot,
  QuickAccessBar,
  QuickAccessScrollArea,
  QuickAccessScrollContent,
  QuickAccessScrollWrapper,
  SFlex,
  SubCategory,
} from './QuickAccess.styled';
import { EMPTY_LINK, LinkArray, QuickAccessItem, useQuickAccessLinks } from './useQuickAccessLinks';

export const useResetQuickAccessCategories = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      dispatch(setQuickAccessActiveCategory(undefined));
      dispatch(setQuickAccessActiveSubCategory(undefined));
    };
    // eslint-disable-next-line
  }, []);
};

const Category = ({
  title,
  categoryKey,
  icon,
  activeCategory,
  link,
  firstSubcategory,
}: {
  title: string;
  categoryKey: string;
  icon: IconType;
  activeCategory: string | undefined;
  link?: string;
  firstSubcategory?: QuickAccessItem;
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isLarge = useLarge();
  const bps = useBreakpointSelector();
  const width = bps([100, 140]);
  const urlParams = new URLSearchParams(location.search);
  const linkWithParams = `${link}?${urlParams}`;
  const vehicle = useSelector(getLastVehicleDetail);
  const subcategoryLinkArray = vehicle?.catalogSource === 'IAM' ? firstSubcategory?.linkIam : firstSubcategory?.linkDh;
  const getSubcategoryLink = () =>
    vehicle && subcategoryLinkArray && subcategoryLinkArray !== EMPTY_LINK
      ? `${getCatalogSourceUrl(vehicle.catalogSource)}/${
          vehicle.vehicleKey
        }${ROUTER_CATALOG_VEHICLE}/${subcategoryLinkArray.join('/')}?${urlParams}`
      : `${firstSubcategory?.link}?${urlParams}`;

  const handleClickOnCategory = () => {
    dispatch(setQuickAccessActiveCategory(categoryKey));
    if (firstSubcategory) {
      dispatch(setQuickAccessActiveSubCategory(firstSubcategory.key));
      history.push(getSubcategoryLink());
    } else if (linkWithParams) {
      history.push(linkWithParams);
    } else {
      return;
    }
  };

  return (
    <WithTooltip title={title} placement={'top'}>
      <div data-testid={`qa-tab-${categoryKey}`} data-active={String(activeCategory === categoryKey)}>
        <CategoryFlex
          isActive={activeCategory === categoryKey}
          direction={'column'}
          justify={'center'}
          align={'center'}
          minWidth={width}
          maxWidth={width}
          minHeight={QUICK_ACCESS_CATEGORY_HEIGHT}
          maxHeight={QUICK_ACCESS_CATEGORY_HEIGHT}
          onClick={handleClickOnCategory}
        >
          <Icon IconComponent={icon} size={32} />
          {isLarge && (
            <Flex maxWidth={width} size={0}>
              <Text type="base" noWrap ellipsis>
                {title}
              </Text>
            </Flex>
          )}
        </CategoryFlex>
      </div>
    </WithTooltip>
  );
};

const Subcategory = ({
  label,
  subCategoryKey,
  linkIam,
  linkDh,
  link,
  initialTextFilters,
  activeSubCategory,
}: {
  label: string;
  subCategoryKey: string;
  linkIam: LinkArray;
  linkDh: LinkArray;
  link?: string;
  initialTextFilters?: Map<string, string[]>;
  activeSubCategory: string | undefined;
}) => {
  const dispatch = useDispatch();
  const vehicle = useSelector(getLastVehicleDetail);
  const urlParams = new URLSearchParams(location.search);
  const isActive = subCategoryKey === activeSubCategory;
  const categoryLinkArray = vehicle?.catalogSource === 'IAM' ? linkIam : linkDh;
  const isOEM = useSelector(getIamCatalogBrandsView) === OEM_BRAND_KEY;

  useEffect(() => {
    if (isActive && initialTextFilters) {
      dispatch(
        setIamInitialTextFilter({
          initialTextFilters,
          filterType: isOEM ? OEM_BRAND_KEY : OTHER_BRAND_KEY,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isOEM]);

  if (!vehicle) {
    return null;
  }

  const categoryLink = link
    ? `${link}?${urlParams}`
    : `${getCatalogSourceUrl(vehicle.catalogSource)}/${
        vehicle.vehicleKey
      }${ROUTER_CATALOG_VEHICLE}/${categoryLinkArray.join('/')}?${urlParams}`;

  return (
    <Link to={categoryLink}>
      <SubCategory
        isActive={isActive}
        data-testid={`qa-subcategory-${subCategoryKey}`}
        data-active={String(isActive)}
        onClick={() => {
          dispatch(setQuickAccessActiveSubCategory(subCategoryKey));
        }}
      >
        <Flex minHeight={33} align={'center'}>
          <Text type="base" displayStyle={isActive && 'link'}>
            {label}
          </Text>
        </Flex>
      </SubCategory>
    </Link>
  );
};

function resolveActiveQuickAccess({
  categories,
  carPartGroupId,
  categoryId,
  subcategoryId,
  isIAM,
  isTreeLoaded,
  storedCategory,
  storedSubCategory,
}: {
  categories: ReturnType<typeof useQuickAccessLinks>;
  carPartGroupId: string;
  categoryId: string;
  subcategoryId: string;
  isIAM: boolean;
  isTreeLoaded: boolean;
  storedCategory: string | undefined;
  storedSubCategory: string | undefined;
}): { activeCategory: string | undefined; activeSubCategory: string | undefined } {
  // While tree is still loading, fall back to stored Redux state to avoid a flash.
  if (!isTreeLoaded) {
    return { activeCategory: storedCategory, activeSubCategory: storedSubCategory };
  }

  // Match every category whose link exactly equals the current URL path segments.
  const urlMatches = categories.flatMap((c): { categoryKey: string; subCategoryKey?: string }[] => {
    const items = c.items ?? [];
    if (items.length > 0) {
      return items
        .filter((i) => {
          const itemLink = isIAM ? i.linkIam : i.linkDh;
          return (
            !i.link &&
            itemLink !== EMPTY_LINK &&
            itemLink[0] === carPartGroupId &&
            itemLink[1] === categoryId &&
            itemLink[2] === subcategoryId
          );
        })
        .map((i) => ({ categoryKey: c.key, subCategoryKey: i.key }));
    }
    const link = isIAM ? c.linkIam : c.linkDh;
    return link &&
      link !== EMPTY_LINK &&
      link[0] === carPartGroupId &&
      link[1] === categoryId &&
      link[2] === subcategoryId
      ? [{ categoryKey: c.key, subCategoryKey: undefined }]
      : [];
  });

  // Prefer the tab the user explicitly clicked (storedCategory) as tiebreaker for shared URLs
  // (e.g. oil_filter appears in both Filter and Oil).
  const urlMatch =
    urlMatches.find((m) => m.categoryKey === storedCategory && m.subCategoryKey === storedSubCategory) ??
    urlMatches.find((m) => m.categoryKey === storedCategory) ??
    urlMatches.find((m) => m.subCategoryKey === storedSubCategory) ??
    urlMatches[0];

  // If no URL match, keep stored state only when on a custom-link subcategory page (e.g. Tires).
  const storedSubHasCustomLink = categories
    .find((c) => c.key === storedCategory)
    ?.items?.find((i) => i.key === storedSubCategory)?.link;

  return {
    activeCategory: urlMatch?.categoryKey ?? (storedSubHasCustomLink ? storedCategory : undefined),
    activeSubCategory: urlMatch?.subCategoryKey ?? (storedSubHasCustomLink ? storedSubCategory : undefined),
  };
}

const QuickAccess = () => {
  const { subcategoryId, carPartGroupId, categoryId } = useParams<{
    subcategoryId: string;
    carPartGroupId: string;
    categoryId: string;
  }>();
  const { activeCategory: storedCategory, activeSubCategory: storedSubCategory } = useSelector(getActiveQuickAccess);
  const vehicle = useSelector(getLastVehicleDetail);
  const tires = useSelector(getVehicleTires);
  const iamTires = useSelector(getIAMTires);
  const iamTree = useSelector((state: RootState) => getExplodedIAMTree(state, vehicle?.vehicleKey));
  const dhTree = useSelector((state: RootState) => getExplodedTree(state, vehicle?.vehicleKey));
  const categories = useQuickAccessLinks();

  const isTreeLoaded = dhTree?.searchStatus !== LOADING && iamTree?.searchStatus !== LOADING;
  const { activeCategory, activeSubCategory } = resolveActiveQuickAccess({
    categories,
    carPartGroupId,
    categoryId,
    subcategoryId,
    isIAM: vehicle?.catalogSource === 'IAM',
    isTreeLoaded,
    storedCategory,
    storedSubCategory,
  });

  const subcategories = categories.find((c) => c.key === activeCategory)?.items;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const { events } = useDraggable(scrollContainerRef as React.MutableRefObject<HTMLElement>);
  const bps = useBreakpointSelector();
  const categoryWidth = bps([100, 140]) ?? 140;
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const isQuickAccessLoading = isLoading(tires) || isLoading(iamTires) || isLoading(iamTree) || isLoading(dhTree);

  const updateScrollButtonStates = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const overflow = scrollWidth > clientWidth + 1;
    setHasOverflow(overflow);
    setIsLeftDisabled(scrollLeft <= 1);
    setIsRightDisabled(scrollLeft + clientWidth >= scrollWidth - 1);
  }, []);

  useEffect(() => {
    const scheduleUpdate = () => {
      requestAnimationFrame(updateScrollButtonStates);
    };

    scheduleUpdate();
    const container = scrollContainerRef.current;
    const content = scrollContentRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => updateScrollButtonStates();
    container.addEventListener('scroll', handleScroll);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(container);
    if (content) {
      resizeObserver.observe(content);
      Array.from(content.children).forEach((child) => resizeObserver.observe(child));
    }

    window.addEventListener('resize', scheduleUpdate);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [categories.length, isQuickAccessLoading, updateScrollButtonStates]);

  const scrollByStep = useCallback(
    (direction: 'left' | 'right') => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }

      const scrollAmount =
        direction === 'left' ? container.scrollLeft - categoryWidth : container.scrollLeft + categoryWidth;
      container.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    },
    [categoryWidth],
  );

  const handleScrollLeft = useCallback(() => scrollByStep('left'), [scrollByStep]);
  const handleScrollRight = useCallback(() => scrollByStep('right'), [scrollByStep]);

  const showLeftArrow = hasOverflow && !isLeftDisabled;
  const showRightArrow = hasOverflow && !isRightDisabled;
  const isCentered = !hasOverflow;

  const renderCategories = () => {
    if (isQuickAccessLoading) {
      return (
        <CenterFlex style={{ height: QUICK_ACCESS_CATEGORY_HEIGHT }}>
          <CenteredSpin />
        </CenterFlex>
      );
    }

    return categories.map((category) => {
      const [firstSubcategory] = category.items || [];
      return (
        <div key={category.key}>
          <Category
            key={category.key}
            title={category.title}
            categoryKey={category.key}
            icon={category.icon}
            activeCategory={activeCategory}
            link={category.link}
            firstSubcategory={firstSubcategory}
          />
        </div>
      );
    });
  };

  return (
    <>
      <Box width={'100%'}>
        <QuickAccessBar data-testid={'quick-access-bar'}>
          {showLeftArrow && (
            <QuickAccessArrowSlot $side={'left'} data-testid={'quick-access-arrow-left'}>
              <QuickAccessScrollBackButton onClick={handleScrollLeft} data-cy={'quick-access-scroll-back'} />
            </QuickAccessArrowSlot>
          )}
          <QuickAccessScrollWrapper $centered={isCentered} data-testid={'quick-access-scroll-wrapper'}>
            <QuickAccessScrollArea
              ref={scrollContainerRef}
              data-testid={'quick-access-scroll-area'}
              $centered={isCentered}
              {...events}
            >
              <QuickAccessScrollContent ref={scrollContentRef}>{renderCategories()}</QuickAccessScrollContent>
            </QuickAccessScrollArea>
          </QuickAccessScrollWrapper>
          {showRightArrow && (
            <QuickAccessArrowSlot $side={'right'} data-testid={'quick-access-arrow-right'}>
              <QuickAccessScrollForwardButton onClick={handleScrollRight} data-cy={'quick-access-scroll-forward'} />
            </QuickAccessArrowSlot>
          )}
        </QuickAccessBar>
      </Box>
      {activeCategory && !isQuickAccessLoading && subcategories && subcategories?.length > 0 && (
        <SFlex minHeight={65} size={0} gap={15} align={'center'} wrap={'nowrap'}>
          {subcategories?.map((sc) => (
            <Subcategory
              key={sc.key}
              subCategoryKey={sc.key}
              label={sc.label}
              link={sc.link}
              initialTextFilters={sc.params}
              linkIam={sc.linkIam}
              linkDh={sc.linkDh}
              activeSubCategory={activeSubCategory}
            />
          ))}
        </SFlex>
      )}
    </>
  );
};

export default QuickAccess;
